import { diff as jsonDiff, type Delta } from 'jsondiffpatch';
import { log, logUnknownErrorResponse } from '@hungryroot/o11y';
import {
  type OccasionPreferenceOptions,
  StatusCode,
  type LikeDislikeTagsRatings,
  type OccasionPreferenceEntity,
  type PatchedCustomerOccasionEntity,
  type CustomerOccasionEntity,
  type ContainerFrequencyOptions,
} from '@hungryroot/rest';
import { type CustomerWithExperiments } from '@hungryroot/rest/api/get_customer/get';
import { patchApiV2CustomersCustomerIdDietaryRestrictions } from '@hungryroot/rest/api/v2/customers/[id]/dietary_restrictions/patch';
import {
  patchApiV2CustomersCustomerIdDishTypes,
  type PatchedCustomerDishType as DishTypeResponse,
} from '@hungryroot/rest/api/v2/customers/[id]/dish_types/patch';
import { getApiV2CustomersOccasionEntityPreferences } from '@hungryroot/rest/api/v2/customers/[id]/occasion_entity_preferences/get/get-api-v2-customers-customer_id-occasion_entity_preferences';
import { patchApiV2CustomersOccasionEntityPreferences } from '@hungryroot/rest/api/v2/customers/[id]/occasion_entity_preferences/patch/patch-api-v2-customers-customer_id-occasion_entity_preferences';
import { postApiV2CustomersOccasionEntityPreferencesBulkDelete } from '@hungryroot/rest/api/v2/customers/[id]/occasion_entity_preferences/post/post-api-v2-customers-customer_id-occasion_entity_preferences-bulk_delete';
import { putApiV2CustomersCustomerIdOccasionGoals } from '@hungryroot/rest/api/v2/customers/[id]/occasion_goals/put';
import { patchApiV2CustomersCustomerIdQuizzes } from '@hungryroot/rest/api/v2/customers/[id]/quizzes/patch';
import { postApiV2CustomersCustomerIdQuizzes } from '@hungryroot/rest/api/v2/customers/[id]/quizzes/post';
import { patchApiV2CustomersCustomerIdTags } from '@hungryroot/rest/api/v2/customers/[id]/tags/patch';
import { getApiV2OccasionPreferenceOptions } from '@hungryroot/rest/api/v2/occasion_preference_options/get/get-api-v2-occasion_preference_options';
import {
  Goals,
  type HouseholdSize,
  EatingHabit,
  Dietary,
  DietaryRestrictions,
  type Quiz,
} from '../defs/quiz';
import { getOccasionPreferenceDietaryRestrictionParamFromQuiz } from '../utils/getOccasionPreferenceDietaryRestrictionParamFromQuiz';
import { toPascalCase } from '../utils/toPascalCase';

function getGoalsPayload(quiz: Quiz) {
  // Excluding removed goals in case anonymous users have this data stored in local storage from before the experiment starts
  return {
    occasion_goals: quiz.goals
      ? quiz.goals
          .filter(
            (goal) =>
              goal !== Goals.EatLessTakeout &&
              goal !== Goals.EatMoreVeggiesWithFamily
          )
          .map((id) => {
            return { id };
          })
      : [],
  };
}

interface CustomersQuizzes {
  customer_id: CustomerWithExperiments['id'];
  household_size: HouseholdSize | 0;
  num_adults: number;
  num_young_children: number;
  num_older_children: number;
  weekly_grocery_spend: number | null;
  breakfast_meal_snack: null;
  lunch_meal_snack: null;
  dinner_meal_snack: null;
  breakfast_frequency: ContainerFrequencyOptions | null;
  lunch_frequency: ContainerFrequencyOptions | null;
  dinner_frequency: ContainerFrequencyOptions | null;
  snack_frequency: ContainerFrequencyOptions | null;
  sweets_frequency: ContainerFrequencyOptions | null;
  fruit_frequency: null;
}

interface QuizOptions {
  originalQuiz?: Quiz;
}

type OccasionGoalsData = {
  occasion_goals: { id: number; name: string }[];
};

export function getNumAdults(quiz: Quiz) {
  // `quiz.adultsCount` should be present as of 08/2024, but below we'll handle cases where legacy customers
  // are trying to finish signup
  if (quiz.adultsCount) {
    return quiz.adultsCount;
  }

  if (!quiz.householdSize) {
    return 0;
  } else if (quiz.householdSize <= 2) {
    return quiz.householdSize;
  }
  return 1;
}

export function getTotalPeople(quiz: Quiz) {
  const { adultsCount, childrenCount } = quiz;

  return (adultsCount ?? 0) + (childrenCount ?? 0);
}

/**
 * The BE only allows values 1, 2, or 3 for household size. If the total people is greater than 3, we'll cap it at 3.
 */
export function deriveHouseholdSizeFromTotalPeople(
  totalPeople: number
): HouseholdSize {
  if (totalPeople > 3) {
    return 3;
  } else if ([1, 2, 3].includes(totalPeople)) {
    return totalPeople;
  }

  return 1;
}

export function getCustomersQuizzesPayload(
  quiz: Quiz,
  customerId: CustomerWithExperiments['id']
): CustomersQuizzes {
  // If this value is available, we will use it, but in some scenarios it's inferred based on total people
  let householdSize = quiz.householdSize;

  // In Exp. 160, we populate quiz.householdSize so that we never need to infer it.
  //
  // In some historic scenarios, we infer it once when the user signs up, but then it can get out of sync,
  // if they go back to the quiz and change the number of people in their household. We'll keep that behavior for now.
  if (householdSize === null) {
    // Infer household size based on total adults + children (ages 9 and over)
    const totalPeople = getTotalPeople(quiz);

    householdSize = deriveHouseholdSizeFromTotalPeople(totalPeople);
  }

  const num_adults = getNumAdults(quiz);

  return {
    customer_id: customerId,
    household_size: householdSize,
    num_adults,
    num_young_children: quiz.youngChildrenCount ?? 0,
    num_older_children: quiz.childrenCount ?? 0,
    weekly_grocery_spend: null,
    breakfast_meal_snack: null,
    lunch_meal_snack: null,
    dinner_meal_snack: null,
    breakfast_frequency: quiz.breakfastFrequency,
    lunch_frequency: quiz.lunchFrequency,
    dinner_frequency: quiz.dinnerFrequency,
    snack_frequency: quiz.snacksFrequency,
    sweets_frequency: quiz.sweetsFrequency,
    fruit_frequency: null,
  };
}

export function getDietaryPreferencesPayload(
  customerId: number,
  quiz: Quiz
): {
  id: DietaryRestrictions;
  selected: boolean;
}[] {
  const dietaryPreferences: {
    id: DietaryRestrictions;
    selected: boolean;
  }[] = [];

  const habits = Object.keys(EatingHabit).map((habit) => {
    const key = toPascalCase(habit);
    return {
      id: DietaryRestrictions[key as keyof typeof DietaryRestrictions],
      selected: false,
    };
  });

  if (quiz.habits) {
    const key = toPascalCase(quiz.habits);
    const filteredHabits = habits.filter(
      (habit) =>
        habit.id !==
        DietaryRestrictions[key as keyof typeof DietaryRestrictions]
    );
    dietaryPreferences.push(...filteredHabits, {
      id: DietaryRestrictions[key as keyof typeof DietaryRestrictions],
      selected: true,
    });
  } else {
    dietaryPreferences.push(...habits);
  }

  const dietary = Object.keys(Dietary).map((habit) => {
    const key = toPascalCase(habit);
    return {
      id: DietaryRestrictions[key as keyof typeof DietaryRestrictions],
      selected: false,
    };
  });

  if (quiz.dietary) {
    const quizDietary = quiz.dietary.map((dietaryPref) => {
      const key = toPascalCase(dietaryPref);
      return {
        id: DietaryRestrictions[key as keyof typeof DietaryRestrictions],
        selected: true,
      };
    });
    const filteredDietary = dietary.filter((obj1) => {
      return !quizDietary.some((obj2) => {
        return obj1.id === obj2.id;
      });
    });
    dietaryPreferences.push(...filteredDietary, ...quizDietary);
  }
  log('quiz_dietary_restrictions_format', {
    customerId,
    description:
      'formatting the data to be used in dietary restrictions patch, as well as the quiz',
    dietaryPreferences,
    quiz,
  });
  return dietaryPreferences;
}

export function getDishTypesPayload(
  quiz: Quiz,
  customerId: CustomerWithExperiments['id']
): DishTypeResponse[] {
  const dishTypes: DishTypeResponse[] = [];
  quiz.lunchDinner?.map((dishType) => {
    dishTypes.push({ customer_id: customerId, dish_type: dishType, rating: 1 });
  });
  return dishTypes;
}

interface TagResponse {
  tag: number;
  preference: LikeDislikeTagsRatings;
  is_explicit: boolean;
}

interface TagResponseWithCustomerId extends TagResponse {
  customer_id: CustomerWithExperiments['id'];
}

export function getTagsPayload(
  quiz: Quiz,
  customerId: CustomerWithExperiments['id'],
  isDietDrivenPreferencesData: boolean
): TagResponseWithCustomerId[] {
  const tags: TagResponseWithCustomerId[] = [];

  quiz.cuisines?.map((tag) => {
    tags.push({
      customer_id: customerId,
      tag: tag.id,
      is_explicit: true,
      preference: tag.liked,
    });
  });
  quiz.flavors?.map((tag) => {
    tags.push({
      customer_id: customerId,
      tag: tag.id,
      is_explicit: true,
      preference: tag.liked,
    });
  });
  quiz.meats?.map((tag) => {
    tags.push({
      customer_id: customerId,
      tag: tag.id,
      is_explicit: true,
      preference: tag.rated,
    });
  });
  quiz.seafood?.map((tag) => {
    tags.push({
      customer_id: customerId,
      tag: tag.id,
      is_explicit: true,
      preference: tag.rated,
    });
  });
  quiz.plants?.map((tag) => {
    tags.push({
      customer_id: customerId,
      tag: tag.id,
      is_explicit: true,
      preference: tag.rated,
    });
  });
  quiz.dislike?.map((id) => {
    tags.push({
      customer_id: customerId,
      tag: id,
      is_explicit: true,
      preference: -1,
    });
  });
  if (!isDietDrivenPreferencesData) {
    quiz.breakfast?.map((id) => {
      tags.push({
        customer_id: customerId,
        tag: id,
        is_explicit: true,
        preference: 1,
      });
    });

    quiz.lunch?.map((id) => {
      tags.push({
        customer_id: customerId,
        tag: id,
        is_explicit: true,
        preference: 1,
      });
    });

    quiz.dinner?.map((id) => {
      tags.push({
        customer_id: customerId,
        tag: id,
        is_explicit: true,
        preference: 1,
      });
    });

    quiz.snacks?.map((id) => {
      tags.push({
        customer_id: customerId,
        tag: id,
        is_explicit: true,
        preference: 1,
      });
    });

    quiz.sweets?.map((id) => {
      tags.push({
        customer_id: customerId,
        tag: id,
        is_explicit: true,
        preference: 1,
      });
    });
  }

  quiz.macroNutrient?.map((id) => {
    tags.push({
      customer_id: customerId,
      tag: id,
      is_explicit: true,
      preference: 1,
    });
  });

  quiz.foodQualities
    ?.filter((id) => id !== null)
    .map((id) => {
      tags.push({
        customer_id: customerId,
        tag: id,
        is_explicit: true,
        preference: 1,
      });
    });

  quiz.foodFormats
    ?.filter((id) => id !== null)
    .map((id) => {
      tags.push({
        customer_id: customerId,
        tag: id,
        is_explicit: true,
        preference: 1,
      });
    });

  quiz.readyMade?.map((id) => {
    tags.push({
      customer_id: customerId,
      tag: id,
      is_explicit: true,
      preference: 1,
    });
  });

  if (quiz.budget !== undefined && quiz.budget !== null) {
    tags.push({
      customer_id: customerId,
      tag: quiz.budget,
      is_explicit: true,
      preference: 1,
    });
  }

  return tags;
}

function indexTagResponses(
  tags: TagResponseWithCustomerId[]
): Record<string, TagResponseWithCustomerId> {
  return tags.reduce(
    (seed, tag) => {
      seed[tag.tag] = tag;
      return seed;
    },
    {} as Record<string, TagResponseWithCustomerId>
  );
}

export function renderTagsPayloadDiff(
  original: Record<string, TagResponseWithCustomerId>,
  diff: Delta
): TagResponseWithCustomerId[] {
  const responses: TagResponseWithCustomerId[] = [];

  Object.keys(diff).forEach((key) => {
    const value = diff[key] as
      | {
          preference?: [
            TagResponseWithCustomerId['preference'],
            TagResponseWithCustomerId['preference'],
          ];
        }
      | [TagResponseWithCustomerId]
      | [TagResponseWithCustomerId, 0, 0];

    // Item inserted or removed
    if (Array.isArray(value)) {
      // Item removed
      if (value[1] === 0 && value[2] === 0) {
        const originalValue = original[key];
        const { customer_id, tag, is_explicit } = originalValue;
        responses.push({
          customer_id,
          tag,
          is_explicit,
          preference: 0,
        });
        return;
      }

      responses.push(value[0]);
      return;
    }

    const originalValue = original[key];
    const { customer_id, tag, is_explicit } = originalValue;

    const { preference } = value;
    if (preference !== undefined) {
      // Tag preferenced changed, record id
      responses.push({
        customer_id,
        tag,
        is_explicit,
        preference: preference[1],
      });
      return;
    }
  });

  return responses;
}

export function diffDishtypePreferencesPayload(
  customerId: number,
  diff: Delta
): DishTypeResponse[] {
  const responses: DishTypeResponse[] = [];

  Object.keys(diff)
    .filter((item) => item !== '_t')
    .forEach((key) => {
      const value = diff[key];
      // Item moved
      if (key.startsWith('_')) {
        const [dishTypeId, from, to] = value;

        // Item removed
        if (from === 0 && to === 0) {
          responses.push({
            customer_id: customerId,
            dish_type: dishTypeId,
            rating: 0,
          });
          return;
        }
      }

      // Item added or modified
      const [originalValue, modifiedValue] = value;
      if (modifiedValue === undefined) {
        // Item was added
        responses.push({
          customer_id: customerId,
          dish_type: originalValue,
          rating: 1,
        });
        return;
      }
    });

  return responses;
}

function persistCustomerQuizzes(
  customerId: number,
  quiz: Quiz,
  options?: QuizOptions
): Promise<unknown> | null {
  const originalQuiz = options?.originalQuiz;

  if (
    originalQuiz === undefined ||
    originalQuiz.householdSize === null ||
    originalQuiz.householdSize === undefined
  ) {
    return postApiV2CustomersCustomerIdQuizzes({
      quiz: getCustomersQuizzesPayload(quiz, customerId),
    });
  }

  const quizzesPayload = getCustomersQuizzesPayload(quiz, customerId);

  if (
    jsonDiff(
      quizzesPayload,
      getCustomersQuizzesPayload(originalQuiz, customerId)
    )
  ) {
    return patchApiV2CustomersCustomerIdQuizzes({
      quiz: quizzesPayload,
    });
  }

  return null;
}

function persistGoals(
  customerId: number,
  quiz: Quiz,
  originalQuiz?: Quiz
): Promise<unknown> | null {
  const goalsPayload = getGoalsPayload(quiz);

  if (originalQuiz === undefined) {
    return putApiV2CustomersCustomerIdOccasionGoals({
      requestBody: goalsPayload,
    });
  }

  if (jsonDiff(goalsPayload, getGoalsPayload(originalQuiz)) !== undefined) {
    return putApiV2CustomersCustomerIdOccasionGoals({
      requestBody: goalsPayload,
    });
  }

  return null;
}

function persistDietaryPreferences(
  customerId: number,
  quiz: Quiz,
  originalQuiz?: Quiz
): Promise<unknown> | null {
  const dietaryPreferencesPayload = getDietaryPreferencesPayload(
    customerId,
    quiz
  );

  log('quiz_dietary_restrictions_patch', {
    customerId,
    description: 'body request data used for dietary restrictions patch',
    dietaryPreferencesPayload,
  });

  if (originalQuiz === undefined) {
    return patchApiV2CustomersCustomerIdDietaryRestrictions({
      requestBody: {
        dietary_restrictions: dietaryPreferencesPayload,
      },
    });
  }

  if (
    jsonDiff(
      dietaryPreferencesPayload,
      getDietaryPreferencesPayload(customerId, originalQuiz)
    ) !== undefined
  ) {
    return patchApiV2CustomersCustomerIdDietaryRestrictions({
      requestBody: {
        dietary_restrictions: dietaryPreferencesPayload,
      },
    });
  }
  return null;
}

function persistDishTypes(
  customerId: number,
  quiz: Quiz,
  originalQuiz?: Quiz
): Promise<unknown> | null {
  const filteredDishTypeQuiz =
    quiz.lunchDinner !== null && quiz.lunchDinner.length > 0
      ? quiz.lunchDinner.filter((dishtype) => dishtype !== 18)
      : [];
  const filteredQuiz = { ...quiz, lunchDinner: filteredDishTypeQuiz };

  if (originalQuiz === undefined) {
    return patchApiV2CustomersCustomerIdDishTypes({
      requestBody: getDishTypesPayload(filteredQuiz, customerId),
    });
  }

  const filteredDishTypeOriginalQuiz =
    originalQuiz &&
    originalQuiz.lunchDinner !== null &&
    originalQuiz.lunchDinner.length > 0
      ? originalQuiz.lunchDinner.filter((dishtype) => dishtype !== 18)
      : [];
  const filteredOriginalQuiz = {
    ...originalQuiz,
    lunchDinner: filteredDishTypeOriginalQuiz,
  };
  const dishTypesDiff = jsonDiff(
    filteredOriginalQuiz.lunchDinner,
    filteredQuiz.lunchDinner
  );
  if (dishTypesDiff !== undefined) {
    return patchApiV2CustomersCustomerIdDishTypes({
      requestBody: diffDishtypePreferencesPayload(customerId, dishTypesDiff),
    });
  }

  if (originalQuiz === undefined) {
    patchApiV2CustomersCustomerIdDishTypes({
      requestBody: getDishTypesPayload(quiz, customerId),
    });
  }

  return null;
}

function persistTags(
  customerId: number,
  quiz: Quiz,
  isDietDrivenPreferencesData: boolean,
  originalQuiz?: Quiz
): Promise<unknown> | null {
  if (originalQuiz === undefined) {
    return patchApiV2CustomersCustomerIdTags({
      requestBody: getTagsPayload(
        quiz,
        customerId,
        isDietDrivenPreferencesData
      ),
    });
  }

  const originalIndexedTags = indexTagResponses(
    getTagsPayload(originalQuiz, customerId, isDietDrivenPreferencesData)
  );

  const tagsDiff = jsonDiff(
    originalIndexedTags,
    indexTagResponses(
      getTagsPayload(quiz, customerId, isDietDrivenPreferencesData)
    )
  );

  if (tagsDiff !== undefined) {
    const diffedPayload = renderTagsPayloadDiff(originalIndexedTags, tagsDiff);
    return patchApiV2CustomersCustomerIdTags({
      requestBody: diffedPayload,
    });
  }
  return null;
}

async function getDietDrivenPreferencesData(quiz: Quiz) {
  const dietaryRestrictionParam =
    getOccasionPreferenceDietaryRestrictionParamFromQuiz(quiz);

  const res =
    dietaryRestrictionParam !== undefined
      ? await getApiV2OccasionPreferenceOptions({
          dietaryRestriction: dietaryRestrictionParam,
        })
      : undefined;

  if (quiz.habits !== null && res?.status !== StatusCode.SuccessOK) {
    logUnknownErrorResponse(
      'Failed to fetch occasion preference options in server persist',
      {
        key: 'quiz.serverPersist.occasionPreferences.fetch',
        response: res,
        dietaryRestriction: quiz.habits,
      }
    );
  }

  const { data: occassionPreferenceOptions } = res || {};

  const occasionPreferenceDataById = {
    breakfast: occassionPreferenceOptions?.breakfast?.map(
      (option) => option.object_id
    ),
    lunch: occassionPreferenceOptions?.lunch?.map((option) => option.object_id),
    dinner: occassionPreferenceOptions?.dinner?.map(
      (option) => option.object_id
    ),
    snacks: occassionPreferenceOptions?.snack?.map(
      (option) => option.object_id
    ),
    sweets: occassionPreferenceOptions?.sweet?.map(
      (option) => option.object_id
    ),
  };

  let isDietDrivenPreferencesData = false;

  switch (true) {
    case occasionPreferenceDataById.breakfast?.some(
      (id) => quiz.breakfast?.includes(id)
    ):
    case occasionPreferenceDataById.lunch?.some(
      (id) => quiz.lunch?.includes(id)
    ):
    case occasionPreferenceDataById.dinner?.some(
      (id) => quiz.dinner?.includes(id)
    ):
    case occasionPreferenceDataById.snacks?.some(
      (id) => quiz.snacks?.includes(id)
    ):
    case occasionPreferenceDataById.sweets?.some(
      (id) => quiz.sweets?.includes(id)
    ):
      isDietDrivenPreferencesData = true;
  }

  return {
    isDietDrivenPreferencesData,
    occassionPreferenceOptions,
  };
}

function getArrayDifferences(arr1: number[], arr2: number[]) {
  const set2 = new Set(arr2);
  const set1 = new Set(arr1);

  return {
    deleted: arr1.filter((item) => !set2.has(item)),
    added: arr2.filter((item) => !set1.has(item)),
  };
}

async function getDietDrivenPreferencesPayload(
  quiz: Quiz,
  isVariant: boolean,
  occassionPreferenceOptions?: OccasionPreferenceOptions
) {
  if (!isVariant || occassionPreferenceOptions === undefined) {
    return {
      dietDrivenPreferencesPatchPayload: undefined,
      dietDrivenPreferencesPostPayload: undefined,
    };
  }

  const occasionPreferenceOptionsById = [
    ...occassionPreferenceOptions.breakfast,
    ...occassionPreferenceOptions.lunch,
    ...occassionPreferenceOptions.dinner,
    ...occassionPreferenceOptions.snack,
    ...occassionPreferenceOptions.sweet,
  ].reduce(
    (acc, preference) => {
      acc[preference.object_id] = preference;
      return acc;
    },
    {} as Record<number, OccasionPreferenceEntity>
  );

  const quizMealTypeQuizDataByObjectId = [
    ...(quiz.breakfast ?? []),
    ...(quiz.lunch ?? []),
    ...(quiz.dinner ?? []),
    ...(quiz.snacks ?? []),
    ...(quiz.sweets ?? []),
  ];

  const res = await getApiV2CustomersOccasionEntityPreferences({
    limit: 500,
  });

  if (res.status !== StatusCode.SuccessOK) {
    logUnknownErrorResponse(
      'Failed to fetch customer occasion preferences in server persist',
      {
        key: 'quiz.serverPersist.customer.occasionPreferences.fetch',
        response: res,
      }
    );
  }

  const { data: customerOccasionPreferences } = res;

  const customerOccasionPreferencesByObjectId =
    customerOccasionPreferences.results.reduce(
      (acc, preference) => {
        acc[preference.object_id] = preference;
        return acc;
      },
      {} as Record<number, CustomerOccasionEntity>
    );

  const { added, deleted } = getArrayDifferences(
    Object.keys(customerOccasionPreferencesByObjectId).map((id) => Number(id)),
    quizMealTypeQuizDataByObjectId
  );

  return {
    dietDrivenPreferencesPatchPayload: added.length
      ? added.map((object_id) => ({
          object_id,
          content_type: occasionPreferenceOptionsById[object_id].type,
        }))
      : undefined,
    dietDrivenPreferencesPostPayload: deleted.length
      ? deleted.map(
          (object_id) => customerOccasionPreferencesByObjectId[object_id].id
        )
      : undefined,
  };
}

function patchDietDrivenPreferences(
  payload: PatchedCustomerOccasionEntity[] | undefined
): Promise<unknown> | null {
  if (payload === undefined) {
    return null;
  }

  return patchApiV2CustomersOccasionEntityPreferences(payload);
}

function deleteDietDrivenPreferences(
  payload: number[] | undefined
): Promise<unknown> | null {
  if (payload === undefined) {
    return null;
  }

  return postApiV2CustomersOccasionEntityPreferencesBulkDelete(payload);
}

export async function serverPersist(
  customerId: number,
  quiz: Quiz,
  options?: QuizOptions
): Promise<void> {
  const promises: Promise<unknown>[] = [];

  const { isDietDrivenPreferencesData, occassionPreferenceOptions } =
    await getDietDrivenPreferencesData(quiz);

  const {
    dietDrivenPreferencesPatchPayload,
    dietDrivenPreferencesPostPayload,
  } = await getDietDrivenPreferencesPayload(
    quiz,
    isDietDrivenPreferencesData,
    occassionPreferenceOptions
  );

  const patchDietDrivenPreferencesPromise = patchDietDrivenPreferences(
    dietDrivenPreferencesPatchPayload
  );
  const deleteDietDrivenPreferencesPromise = deleteDietDrivenPreferences(
    dietDrivenPreferencesPostPayload
  );

  if (patchDietDrivenPreferencesPromise !== null) {
    promises.push(patchDietDrivenPreferencesPromise);
  }
  if (deleteDietDrivenPreferencesPromise !== null) {
    promises.push(deleteDietDrivenPreferencesPromise);
  }

  const persistCustomerQuizzesPromise = persistCustomerQuizzes(
    customerId,
    quiz,
    options
  );

  if (persistCustomerQuizzesPromise !== null) {
    promises.push(persistCustomerQuizzesPromise);
  }

  const persistGoalsPromise = persistGoals(
    customerId,
    quiz,
    options?.originalQuiz
  );

  if (persistGoalsPromise !== null) {
    promises.push(persistGoalsPromise);
  }

  const dietaryPreferencesPromise = persistDietaryPreferences(
    customerId,
    quiz,
    options?.originalQuiz
  );

  if (dietaryPreferencesPromise !== null) {
    promises.push(dietaryPreferencesPromise);
  }

  const dishTypePromise = persistDishTypes(
    customerId,
    quiz,
    options?.originalQuiz
  );

  if (dishTypePromise !== null) {
    promises.push(dishTypePromise);
  }

  const tagsPromise = persistTags(
    customerId,
    quiz,
    isDietDrivenPreferencesData,
    options?.originalQuiz
  );

  if (tagsPromise !== null) {
    promises.push(tagsPromise);
  }

  const result = await Promise.all(promises);

  // The occasion_goals PUT request returns a 200 response even when a given goal is set to inactive in admin. The diff below evaluates that response data to determine if selected goals have all been persisted.
  const occasionGoalsResult = result.find(
    (r): r is { data: OccasionGoalsData } =>
      Boolean(
        r !== null &&
          typeof r === 'object' &&
          'data' in r &&
          r.data?.hasOwnProperty('occasion_goals')
      )
  );
  const persistedGoalsById = occasionGoalsResult?.data?.occasion_goals.map(
    (goal) => goal.id
  );
  const quizGoalsById = getGoalsPayload(quiz).occasion_goals.map(
    (goal) => goal.id
  );

  const inactiveGoals = persistedGoalsById
    ? quizGoalsById.filter((id) => !persistedGoalsById.includes(id))
    : [];

  if (inactiveGoals.length > 0) {
    logUnknownErrorResponse('Failed to persist selected goals in quiz', {
      key: 'quiz.serverPersist.goals',
      customerId,
      inactiveGoals,
    });
  }
}
